
/**
 * 訂單操作日誌元件
 * 顯示所有管理員對訂單的操作紀錄（分配、取消、編輯等）
 */

import { useState, useEffect } from 'react';
import { fetchOrderLogs } from '../../../../../modules/allocation';

interface LogEntry {
  id: string;
  order_id: string;
  action_type: string;
  description: string;
  old_value: string | null;
  new_value: string | null;
  admin_name: string | null;
  admin_id: string | null;
  created_at: string;
}

interface Props {
  orderId: string;
  refreshKey?: number;
}

const ACTION_LABELS: Record<string, { label: string; icon: string; color: string }> = {
  allocation_inventory: {
    label: '庫存分配',
    icon: 'ri-box-3-line',
    color: 'text-emerald-600 bg-emerald-50',
  },
  allocation_external: {
    label: '同行調度',
    icon: 'ri-truck-line',
    color: 'text-sky-600 bg-sky-50',
  },
  allocation_cancel: {
    label: '取消分配',
    icon: 'ri-close-circle-line',
    color: 'text-red-600 bg-red-50',
  },
  order_edit: {
    label: '編輯訂單',
    icon: 'ri-edit-line',
    color: 'text-amber-600 bg-amber-50',
  },
  status_change: {
    label: '狀態變更',
    icon: 'ri-refresh-line',
    color: 'text-indigo-600 bg-indigo-50',
  },
};

export default function OrderLogSection({ orderId, refreshKey }: Props) {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    loadLogs();
  }, [orderId, refreshKey]);

  const loadLogs = async () => {
    setLoading(true);
    try {
      const data = await fetchOrderLogs(orderId);
      setLogs(data);
    } catch (err) {
      console.error('載入日誌失敗:', err);
    } finally {
      setLoading(false);
    }
  };

  const getActionInfo = (actionType: string) => {
    return ACTION_LABELS[actionType] || {
      label: actionType,
      icon: 'ri-file-list-line',
      color: 'text-gray-600 bg-gray-50',
    };
  };

  const displayLogs = expanded ? logs : logs.slice(0, 5);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
          <i className="ri-history-line text-teal-600"></i>
          操作紀錄
        </h2>
        <span className="text-xs text-gray-400">
          共 {logs.length} 筆
        </span>
      </div>

      {loading ? (
        <div className="text-center py-8 text-gray-500">
          <i className="ri-loader-4-line text-2xl animate-spin"></i>
          <p className="mt-2 text-sm">載入中...</p>
        </div>
      ) : logs.length === 0 ? (
        <div className="text-center py-8 text-gray-400">
          <div className="w-10 h-10 flex items-center justify-center mx-auto mb-2">
            <i className="ri-file-list-line text-3xl"></i>
          </div>
          <p className="text-sm">尚無操作紀錄</p>
        </div>
      ) : (
        <>
          <div className="space-y-3">
            {displayLogs.map((log) => {
              const info = getActionInfo(log.action_type);
              return (
                <div key={log.id} className="flex gap-3">
                  {/* 圖示 */}
                  <div className={`w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0 ${info.color}`}>
                    <i className={`${info.icon} text-sm`}></i>
                  </div>
                  {/* 內容 */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-gray-900">
                        {info.label}
                      </span>
                      <span className="text-xs text-gray-400">
                        {new Date(log.created_at).toLocaleString('zh-TW', {
                          month: '2-digit',
                          day: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mt-0.5 break-words">
                      {log.description}
                    </p>
                    {(log.old_value || log.new_value) && (
                      <div className="flex items-center gap-2 mt-1 text-xs">
                        {log.old_value && (
                          <span className="text-red-500 line-through">{log.old_value}</span>
                        )}
                        {log.old_value && log.new_value && (
                          <i className="ri-arrow-right-line text-gray-400"></i>
                        )}
                        {log.new_value && (
                          <span className="text-emerald-600">{log.new_value}</span>
                        )}
                      </div>
                    )}
                    <div className="text-xs text-gray-400 mt-1">
                      <i className="ri-user-line mr-1"></i>
                      {log.admin_name || '系統'}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {logs.length > 5 && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="mt-4 w-full text-center text-sm text-teal-600 hover:text-teal-700 cursor-pointer py-2"
            >
              {expanded ? (
                <>
                  <i className="ri-arrow-up-s-line mr-1"></i>
                  收起
                </>
              ) : (
                <>
                  <i className="ri-arrow-down-s-line mr-1"></i>
                  查看全部 {logs.length} 筆紀錄
                </>
              )}
            </button>
          )}
        </>
      )}
    </div>
  );
}
